import type {
  AccountDailyCondition,
  AccountDailyRaw,
  AdsInsightDailyCondition,
  AdsInsightDailyItem,
  AdsInsightDailyRaw,
  CurrencySpend,
  CustomDimensionReportItem,
  CustomDimensionReportParams,
  CustomDimensionReportResponse,
  CustomDimensionSummary,
  CustomerDailyCondition,
  CustomerDailyRaw,
  MetaMultiDimensionDashboardApiData,
  MetaMultiDimensionDashboardApiRow,
  MetaMultiDimensionDashboardCondition,
  MetaMultiDimensionDashboardDimensionItem,
  MetaMultiDimensionDashboardResponse,
} from './types'

import type { PagingRequest, PagingResponse } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

function normalizeAdMaterials(materialSpec?: unknown) {
  if (!Array.isArray(materialSpec)) {
    if (typeof materialSpec === 'string' && materialSpec) {
      return [
        {
          id: '0',
          thumbnail: '',
          type: 'url' as const,
          url: materialSpec,
        },
      ]
    }

    return []
  }

  return materialSpec.map((item, index) => {
    const material = item as {
      picture?: string
      type: 'image' | 'url' | 'video'
      url: string
    }

    let thumbnail = material.url
    if (material.type === 'video') {
      thumbnail = material.picture ?? material.url
    } else if (material.type === 'url') {
      thumbnail = ''
    }

    return {
      id: `${index}`,
      thumbnail,
      type: material.type,
      url: material.url,
    }
  })
}

function mapMetaDimensionRows(
  rows?: MetaMultiDimensionDashboardApiRow[] | null,
): MetaMultiDimensionDashboardDimensionItem[] {
  return (rows ?? []).map((item) => ({
    clicks: item.clicks,
    conversions: item.conversions,
    cpa: item.cpa,
    cpc: item.cpc,
    cpm: item.cpm,
    ctr: item.ctr,
    dimensionValue: item.dimensionValue || '未知',
    impressions: item.impressions,
    roas: item.roas,
    spend: item.spend,
    spendPercent: item.spendShare,
  }))
}

function mapMetaDashboardResponse(
  data: MetaMultiDimensionDashboardApiData,
): MetaMultiDimensionDashboardResponse {
  return {
    ageRows: mapMetaDimensionRows(data.age?.rows),
    biddingStrategyRows: mapMetaDimensionRows(data.bidStrategy?.rows),
    countryRows: mapMetaDimensionRows(data.country?.rows),
    genderRows: mapMetaDimensionRows(data.gender?.rows),
    overview: (data.overview ?? []).map((item) => ({
      dimension: item.dimensionLabel || item.dimension || '-',
      topLabel: item.top1Label || '-',
      topSpendPercent: item.top1SpendShare,
    })),
    placementRows: mapMetaDimensionRows(data.platformPosition?.rows).map(
      (item) => ({
        ...item,
        placement: item.dimensionValue,
      }),
    ),
    summary: {
      conversions: data.globalKpi.conversions,
      cpa: data.globalKpi.cpa,
      roas: data.globalKpi.roas,
      totalSpend: data.globalKpi.spend,
    },
  }
}

export const adCustomerPerformanceApi = defineApi('operation', ({ prefix }) => {
  function getAdsInsightDailyList(
    path: string,
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    const condition = params.condition

    return requestClient
      .post<{
        list?: AdsInsightDailyRaw[] | null
        summary: Record<string, number>
        total: number
      }>(prefix(`adsInsightReport/${path}`), {
        condition: {
          accountId: condition.accountId,
          accountName: condition.accountName,
          accountType: condition.accountType,
          ad: condition.ad,
          adset: condition.adset,
          amOpenIds: condition.amOpenIds,
          bdOpenIds: condition.bdOpenIds,
          campaign: condition.campaign,
          countries: condition.countries,
          customerId: condition.customerId,
          customerIds: condition.customerIds,
          customerName: condition.customerName,
          customerStatus: condition.customerStatus,
          dateRange: DateRanges.datesOrNull(condition.dateRange),
          industryLabel: condition.industryLabel,
          industryC1Code: condition.industryC1Code,
          industryC2Code: condition.industryC2Code,
          mediaPlatforms: condition.mediaPlatforms,
        },
        orderBy: params.orderBy ?? condition.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy ?? condition.sortBy,
      })
      .then((res) => ({
        list: (res.list ?? []).map((item) => {
          const materials = normalizeAdMaterials(item.materialSpec)
          return {
            accountId: item.accountId,
            accountName: item.accountName,
            accountType: item.accountType,
            accountNum: item.accountNum,
            adNum: item.adNum,
            adId: item.adId,
            adName: item.adName,
            adStatus: item.adStatus,
            adsetNum: item.adsetNum,
            adsetId: item.adsetId,
            adsetName: item.adsetName,
            adsetStatus: item.adsetStatus,
            bidAmount: item.bidAmount,
            bidStrategy: item.bidStrategy,
            campaignCount: item.campaignCount,
            campaignNum: item.campaignNum,
            campaignId: item.campaignId,
            campaignName: item.campaignName,
            campaignStatus: item.campaignStatus,
            clicks: item.clicks,
            conversionValue: item.conversionValue,
            conversions: item.conversions,
            country: item.country,
            cpa: item.cpa,
            cpc: item.cpc,
            cpm: item.cpm,
            ctr: item.ctr,
            currency: item.currency,
            customerId: item.customerId,
            customerName: item.customerName,
            customerStatus: item.customerStatus,
            cvr: item.cvr,
            creativeBody: item.creativeBody,
            creativeTitle: item.creativeTitle,
            callToAction: item.callToAction,
            managerOpenId: item.managerOpenId,
            reportDate: item.reportDate,
            impressions: item.impressions,
            industryLabel: item.industryLabel,
            industryC1Code: item.industryC1Code,
            industryC1Name: item.industryC1Name,
            industryC2Code: item.industryC2Code,
            industryC2Name: item.industryC2Name,
            landingPageUrl: item.landingPageUrl,
            materialSpec: materials,
            mediaPlatform: item.mediaPlatform,
            merchantId: item.merchantId,
            operatorOpenId: item.operatorOpenId,
            objective: item.objective,
            platform: item.mediaPlatform,
            reach: item.reach,
            roas: item.roas,
            spend: item.spend,
            dailyBudget: item.dailyBudget,
            lifetimeBudget: item.lifetimeBudget,
            spendGrowthRate: item.spendGrowthRate,
            spendYesterday: item.spendYesterday,
            totalSpend: item.totalSpend,
            totalUsdSpend: item.totalUsdSpend,
          } satisfies AdsInsightDailyItem
        }),
        summary: res.summary,
        total: res.total,
      }))
  }

  function getCustomerDailyList(params: PagingRequest<CustomerDailyCondition>) {
    const condition = params.condition

    return requestClient
      .post(prefix('adsRecoReport/customerDaily'), {
        condition: {
          amOpenIds: condition.amOpenIds,
          bdOpenIds: condition.bdOpenIds,
          customerId: condition.customerId,
          customerName: condition.customerName,
          dateRange: DateRanges.datesOrNull(condition.dateRange),
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((res) => ({
        list: (res.list || []).map((item: CustomerDailyRaw) => {
          const currencySpends: CurrencySpend[] = item.currencySpends || []
          const eur = currencySpends.find((v) => v.currency === 'EUR')
          const usd = currencySpends.find((v) => v.currency === 'USD')

          return {
            amOpenIds: item.amOpenId,
            bdOpenIds: item.bdOpenId || [],
            currencySpends,
            customerId: item.customerId ? String(item.customerId) : '',
            customerName: item.customerName,
            date: item.date,
            eurCurrency: eur?.currency,
            eurRateToUsd: eur?.rateToUsd,
            eurSpend: eur?.spend,
            totalUsdSpend: item.totalUsdSpend,
            usdCurrency: usd?.currency,
            usdRateToUsd: usd?.rateToUsd,
            usdSpend: usd?.spend,
          }
        }),
        summary: {
          totalEurSpend: res.summary?.totalEurSpend,
          totalUsdSpendConverted: res.summary?.totalUsdSpendConverted,
          totalUsdSpendPure: res.summary?.totalUsdSpendPure,
        },
        total: res.total,
      }))
  }

  function getAccountDailyList(params: PagingRequest<AccountDailyCondition>) {
    const condition = params.condition

    return requestClient
      .post(prefix('adsRecoReport/accountDaily'), {
        condition: {
          accountId: condition.accountId,
          accountName: condition.accountName,
          accountTypes: condition.accountTypes,
          agencies: condition.agencies,
          amOpenIds: condition.amOpenIds,
          bdOpenIds: condition.bdOpenIds,
          customerId: condition.customerId,
          customerName: condition.customerName,
          dateRange: DateRanges.datesOrNull(condition.dateRange),
          mediaPlatforms: condition.mediaPlatforms,
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((res) => ({
        list: (res.list || []).map((item: AccountDailyRaw) => ({
          accountId: item.accountId ? String(item.accountId) : '',
          accountName: item.accountName,
          accountType: item.accountType,
          agency: item.agency,
          amOpenIds: item.amOpenId,
          bdOpenIds: item.bdOpenId,
          customerId: item.customerId ? String(item.customerId) : '',
          customerName: item.customerName,
          date: item.date,
          mediaPlatform: item.mediaPlatform,
          originalCurrency: item.originalCurrency,
          rateToUsd: item.rateToUsd,
          spend: item.spend,
          usdSpend: item.usdSpend,
        })),
        summary: {
          totalSpend: res.summary?.totalSpend,
          totalUsdSpend: res.summary?.totalUsdSpend,
        },
        total: res.total,
      }))
  }

  function getInsightAccountDailyList(
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    return getAdsInsightDailyList('accountDaily', params)
  }

  function getInsightAdDailyList(
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    return getAdsInsightDailyList('adDaily', params)
  }

  function getInsightAdsetDailyList(
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    return getAdsInsightDailyList('adsetDaily', params)
  }

  function getInsightCampaignDailyList(
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    return getAdsInsightDailyList('campaignDaily', params)
  }

  function getInsightCustomerDailyList(
    params: PagingRequest<AdsInsightDailyCondition>,
  ) {
    return getAdsInsightDailyList('customerDaily', params)
  }

  function getMetaMultiDimensionDashboard(
    condition: MetaMultiDimensionDashboardCondition,
  ) {
    return requestClient
      .post<MetaMultiDimensionDashboardApiData>(
        prefix('adsInsightReport/metaBreakdowns'),
        {
          condition: {
            accountId: condition.accountId,
            accountName: condition.accountName,
            adId: condition.adId,
            adName: condition.adName,
            adsetId: condition.adsetId,
            adsetName: condition.adsetName,
            amOpenIds: condition.amOpenIds,
            bdOpenIds: condition.bdOpenIds,
            campaignId: condition.campaignId,
            campaignName: condition.campaignName,
            customerIds: condition.customerIds,
            customerName: condition.customerName,
            dateRange: DateRanges.datesOrNull(condition.dateRange),
          },
        },
      )
      .then((res) => {
        return mapMetaDashboardResponse(res)
      })
  }

  /**
   * 查询自定义维度报表，industry 维度返回一级、二级标签编码和名称。
   * POST /adsInsightReport/customDimensions
   */
  function getCustomDimensionReport(
    params: CustomDimensionReportParams,
  ): Promise<
    PagingResponse<CustomDimensionReportItem> & {
      summary?: CustomDimensionSummary | null
    }
  > {
    return requestClient
      .post<CustomDimensionReportResponse>(
        prefix('adsInsightReport/customDimensions'),
        {
          ...params,
          condition: {
            ...params.condition,
            accountStatus: params.condition.accountStatus?.map(String),
            dateRange: DateRanges.datesOrNull(params.condition.dateRange),
          },
        },
      )
      .then((res) => ({
        list: (res.list ?? []).map((item) => ({
          accountId: item.accountId,
          accountName: item.accountName,
          accountStatus: item.accountStatus,
          accountType: item.accountType,
          agency: item.agency,
          statusLastChangeDate: item.statusLastChangeDate,
          amOpenIds: item.amOpenId,
          adBody: item.adBody,
          adCallToAction: item.adCallToAction,
          adId: item.adId,
          adLandingPageUrl: item.adLandingPageUrl,
          adName: item.adName,
          adReviewFeedback: item.adReviewFeedback,
          adStatus: item.adStatus,
          adTitle: item.adTitle,
          adsetDailyBudget: item.adsetDailyBudget,
          adsetId: item.adsetId,
          adsetLifetimeBudget: item.adsetLifetimeBudget,
          adsetName: item.adsetName,
          adsetStatus: item.adsetStatus,
          bdOpenIds: item.bdOpenId,
          bidStrategy: item.bidStrategy,
          campaignDailyBudget: item.campaignDailyBudget,
          campaignId: item.campaignId,
          campaignLifetimeBudget: item.campaignLifetimeBudget,
          campaignName: item.campaignName,
          campaignStatus: item.campaignStatus,
          clicks: item.clicks,
          conversionValue: item.conversionValue,
          conversions: item.conversions,
          country: item.country,
          cpa: item.cpa,
          cpc: item.cpc,
          cpm: item.cpm,
          ctr: item.ctr,
          cvr: item.cvr,
          customerId: item.customerId,
          customerLevel: item.customerLevel,
          customerName: item.customerName,
          impressions: item.impressions,
          industryC1Code: item.industryC1Code,
          industryC1Name: item.industryC1Name,
          industryC2Code: item.industryC2Code,
          industryC2Name: item.industryC2Name,
          materialSpec: normalizeAdMaterials(item.materialSpec),
          mediaPlatform: item.mediaPlatform,
          objective: item.objective,
          reportDate: item.reportDate,
          reportDateEnd: item.reportDateEnd,
          reportDateStart: item.reportDateStart,
          reportMonth: item.reportMonth,
          reportWeek: item.reportWeek,
          roas: item.roas,
          spend: item.spend,
        })),
        page: res.pageIndex,
        pageSize: res.pageSize,
        summary: res.summary,
        total: res.total,
      }))
  }

  return {
    getAccountDailyList,
    getCustomDimensionReport,
    getCustomerDailyList,
    getInsightAccountDailyList,
    getInsightAdDailyList,
    getInsightAdsetDailyList,
    getInsightCampaignDailyList,
    getInsightCustomerDailyList,
    getMetaMultiDimensionDashboard,
  }
})
